import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';

import { enhanceMentions } from '../../../lib/enhanceMentions';
import { linkifyElement } from '../../../lib/linkify';
import { clsx } from '../../utils/clsx';
import { LinkPreviewList } from '../LinkPreviewList';
import { normalizeComposerOutput, sanitizeComposerHtml } from './utils';
import styles from './TaggableComposerContent.module.css';

const hasRenderablePreviews = (previews) =>
  Array.isArray(previews) && previews.some((preview) => preview && preview.url);

const TaggableComposerContent = ({
  html,
  sanitize = true,
  linkPreviews = [],
  linkPreviewListProps,
  className,
  htmlClassName,
}) => {
  const contentRef = useRef(null);

  const safeHtml = useMemo(() => {
    if (!html) return '';
    return normalizeComposerOutput(html, { sanitize });
  }, [html, sanitize]);

  useEffect(() => {
    if (!safeHtml) return;
    const node = contentRef.current;
    if (!node) return;
    enhanceMentions(node);
    linkifyElement(node);
  }, [safeHtml]);

  const shouldRenderHtml = safeHtml && safeHtml.trim().length > 0;
  const shouldRenderPreviews = hasRenderablePreviews(linkPreviews);
  const listProps = linkPreviewListProps || {};

  if (!shouldRenderHtml && !shouldRenderPreviews) {
    return null;
  }

  return (
    <div className={clsx(styles.root, className)}>
      {shouldRenderHtml ? (
        <div
          ref={contentRef}
          className={clsx(styles.html, htmlClassName)}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      ) : null}
      {shouldRenderPreviews ? <LinkPreviewList previews={linkPreviews} {...listProps} /> : null}
    </div>
  );
};

TaggableComposerContent.propTypes = {
  html: PropTypes.string,
  sanitize: PropTypes.bool,
  linkPreviews: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      siteName: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
  ),
  linkPreviewListProps: PropTypes.shape({
    loading: PropTypes.bool,
    loadingMessage: PropTypes.string,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    onRemove: PropTypes.func,
    size: PropTypes.oneOf(['default', 'compact']),
    className: PropTypes.string,
    messageClassName: PropTypes.string,
  }),
  className: PropTypes.string,
  htmlClassName: PropTypes.string,
};

export default TaggableComposerContent;
